import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { switchMap } from 'rxjs/operators';
import { FormArray, FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { forkJoin, Observable, take } from 'rxjs';

import { ProjectsService } from '../../services/projects.service';
import * as Projects from '../../shared/projects.model';
import { ReclaimVotesModalData } from '../../shared/projects.model';
import { LanguageService } from '../../services/language.service';
import { Constants } from '../../app.constants';
import { ProjectsConstants } from '../projects.constants';

@Component({
  selector: 'app-reclaim-vote-dialog',
  templateUrl: './reclaim-vote-dialog.component.html',
  styleUrls: ['./reclaim-vote-dialog.component.scss']
})
export class ReclaimVoteDialogComponent implements OnInit {

  Constants = Constants;
  ProjectConstants = ProjectsConstants;
  votedProjects$: Observable<Projects.PartialProject[]>;
  projectIdToVote: number;
  noProjectSelected = true;
  selectedProjectsNumber: number
  isMobile = false;
  reclaimProjectForm: FormGroup;
  projects: Projects.PartialProject[] = [];


  @Input() isManagingVotes = false;

  constructor(private dialogRef: MatDialogRef<ReclaimVoteDialogComponent>,
              @Inject(MAT_DIALOG_DATA) data,
              private fb: FormBuilder,
              private projectsService: ProjectsService,
              private languageService: LanguageService
  ) {
    this.projectIdToVote = data.projectId;
    this.votedProjects$ = this.projectsService.supportedProjects$;
    this.reclaimProjectForm = this.fb.group({
      projectId: new UntypedFormControl('', Validators.required),
      projectCheckboxes: this.fb.array([])
    });
  }

  get currentLanguage() {
    return this.languageService.getUserUsedLanguage();
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 768;
    this.votedProjects$.pipe(take(1)).subscribe(projects => {
      this.projects = projects;
      if (this.isManagingVotes) {
        this.addCheckboxes(projects);
      }
    });
  }

  manageVotes() {
    this.noProjectSelected = false;

    const checkboxesArray = this.reclaimProjectForm.get('projectCheckboxes') as FormArray;
    const selectedProjectIds = checkboxesArray.value
      .map((checked, index) => checked ? this.projects[index].id : null)
      .filter(v => v !== null);

    // Create an array of observables for the unvoteProject calls
    const unvoteProjectObservables = selectedProjectIds.map(projectId =>
      this.projectsService.unvoteProject(+projectId)
    );

    // Use forkJoin to execute all observables and wait for all of them to complete
    forkJoin(unvoteProjectObservables).subscribe({
      next: () => {
        const updatedProjects = this.projects.filter(p => selectedProjectIds.includes(p.id));
        this.closeDialog({code: ProjectsConstants.MANAGED_VOTES, projects: updatedProjects});
      },
      error: () => {
        this.closeDialog({code: ProjectsConstants.CANCELLED_ACTION, projects: []});
      }
    });
  }

  closeDialog(parameter: ReclaimVotesModalData) {
    this.dialogRef.close(parameter);
  }

  changeVote() {
    const projectIdToUnvote = this.reclaimProjectForm.get('projectId').value;

    this.projectsService.unvoteProject(projectIdToUnvote)
      .pipe(
        switchMap(() => this.projectsService.voteProject(this.projectIdToVote))
      )
      .subscribe(
        () => {
          const unvotedProject = this.projects.find(p => p.id === projectIdToUnvote);

          this.closeDialog({
            code: ProjectsConstants.RECLAIMED_VOTE,
            projects: unvotedProject ? [unvotedProject] : []
          })
        });
  }

  onCheckboxChange(e: Event, index: number) {
    const projectCheckboxesArray = this.reclaimProjectForm.get('projectCheckboxes') as FormArray;

    // Updates the value of a checkbox in the `projectCheckboxesArray` FormArray.
    //It sets the value of the checkbox to the checked state of the HTMLInputElement.
    projectCheckboxesArray.at(index).setValue((e.target as HTMLInputElement).checked);

    // Update noProjectSelected based on whether any checkbox is checked
    this.noProjectSelected = projectCheckboxesArray.controls.every(control => !control.value);
    this.selectedProjectsNumber = projectCheckboxesArray.controls.filter(control => control.value).length;
  }

  private addCheckboxes(projects: Projects.PartialProject[]) {
    const projectCheckboxes = this.reclaimProjectForm.get('projectCheckboxes') as FormArray;
    projects.forEach(() => {
      projectCheckboxes.push(this.fb.control(false));
    });
  }
}
